import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { fmtMoney, fmtNum } from "@/lib/format";
import StatCard from "@/components/StatCard";
import { Loading, EmptyState } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ProductThumb from "@/components/ProductThumb";
import { DollarSign, Percent, TrendingUp, Banknote, Landmark, CheckCircle2, AlertTriangle } from "lucide-react";

const FEE_COLORS = { referral: "#007AFF", fba: "#F59E0B", other: "#8B5CF6" };

export default function TransactionsTab({ region }) {
  const { currency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [recon, setRecon] = useState({});
  const [skuSort, setSkuSort] = useState({ key: "net_profit", dir: "desc" });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/transactions?region=${region}&currency=${currency}`)
      .then((r) => {
        setData(r.data);
        const init = {};
        r.data.settlements.forEach((s) => { if (s.actual_received != null) init[s.settlement_id] = String(s.actual_received); });
        setRecon(init);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [region, currency]);
  useEffect(load, [load]);

  const saveRecon = async (s) => {
    const raw = recon[s.settlement_id];
    if (raw === undefined || raw === "") return;
    try {
      await api.patch(`/settlements/${encodeURIComponent(s.settlement_id)}/reconcile`, { actual_received: Number(raw) });
      setData((d) => ({ ...d, settlements: d.settlements.map((x) => x.settlement_id === s.settlement_id ? { ...x, actual_received: Number(raw) } : x) }));
      toast.success("Reconciliation saved");
    } catch { toast.error("Could not save."); }
  };

  if (loading) return <Loading />;
  if (!data) return <EmptyState title="Could not load transactions" message="Please try again." />;

  const k = data.kpis;
  const regionLabel = region === "US" ? "Amazon US (USD)" : "Amazon EU (GBP + EUR)";
  const feeParts = [
    { key: "referral", label: "Referral / Selling", value: k.referral },
    { key: "fba", label: "FBA fees", value: k.fba },
    { key: "other", label: "Other fees", value: k.other },
  ];
  const feeTotal = k.fees || 1;
  const skuArrow = (kk) => (skuSort.key === kk ? (skuSort.dir === "asc" ? " ↑" : " ↓") : "");
  const sortSku = (kk) => setSkuSort((s) => ({ key: kk, dir: s.key === kk && s.dir === "desc" ? "asc" : "desc" }));
  const sortedSkus = [...data.sku_profitability].sort((a, b) => {
    const av = a[skuSort.key] ?? 0, bv = b[skuSort.key] ?? 0;
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return skuSort.dir === "asc" ? cmp : -cmp;
  });

  if (!k.orders && !data.settlements.length) {
    return <EmptyState title={`No ${region} transactions yet`} message="Upload an Amazon Settlement / Payments report (or load the sample dataset) to reconcile disbursements and see fees per order." />;
  }

  return (
    <div className="space-y-6" data-testid={`transactions-tab-${region}`}>
      <p className="text-sm text-[#A3A3A3]">{regionLabel} · reconcile disbursements, check fees per order and SKU profitability.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="tx-sales" label="Sales" value={fmtMoney(k.sales, currency, true)} sub={`${fmtNum(k.orders)} orders · ${fmtNum(k.units)} units`} icon={DollarSign} accent="#007AFF" />
        <StatCard testid="tx-fees" label="Amazon Fees" value={fmtMoney(k.fees, currency, true)} sub={`${k.fee_pct}% of sales`} icon={Percent} accent="#F59E0B" />
        <StatCard testid="tx-net" label="Net Profit" value={fmtMoney(k.net_profit, currency, true)} sub="after fees + COGS" icon={TrendingUp} accent="#10B981" />
        <StatCard testid="tx-disbursed" label="Disbursed" value={fmtMoney(k.disbursed, currency, true)} sub={`${fmtNum(k.settlements)} settlements`} icon={Banknote} accent="#8B5CF6" />
      </div>

      {/* Fee composition */}
      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-5" data-testid="tx-fee-breakdown">
        <h3 className="font-head font-bold text-white mb-1">Fee Composition</h3>
        <p className="text-xs text-[#666] mb-4">How Amazon fees break down for {region}</p>
        <div className="h-3 w-full rounded-full overflow-hidden flex bg-[#1a1a1a]">
          {feeParts.map((p) => (
            <div key={p.key} style={{ width: `${(p.value / feeTotal) * 100}%`, backgroundColor: FEE_COLORS[p.key] }} />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          {feeParts.map((p) => (
            <div key={p.key} className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: FEE_COLORS[p.key] }} />
              <div>
                <p className="text-xs text-[#A3A3A3]">{p.label}</p>
                <p className="text-sm font-semibold text-white tnum">{fmtMoney(p.value, currency)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Settlements / disbursements */}
      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="tx-settlements-table">
        <div className="p-5 border-b border-[#1a1a1a] flex items-center gap-2">
          <Landmark className="h-4 w-4 text-[#8B5CF6]" />
          <div><h3 className="font-head font-bold text-white">Disbursements</h3><p className="text-xs text-[#666]">Amounts shown in each settlement's own currency — tick off against your bank deposits</p></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
              <th className="px-5 py-3">Settlement ID</th><th className="px-5 py-3">Cur</th><th className="px-5 py-3">Period</th>
              <th className="px-5 py-3 text-right">Sales</th><th className="px-5 py-3 text-right">Fees</th><th className="px-5 py-3 text-right">Refunds</th><th className="px-5 py-3 text-right">Disbursed</th>
              <th className="px-5 py-3 text-right">Bank Received</th><th className="px-5 py-3">Reconcile</th>
            </tr></thead>
            <tbody>
              {data.settlements.map((s) => {
                const actual = s.actual_received;
                const hasActual = actual != null;
                const diff = hasActual ? +(actual - s.disbursed).toFixed(2) : null;
                const matched = hasActual && Math.abs(diff) <= 0.5;
                return (
                <tr key={s.settlement_id} data-testid={`settlement-row-${s.settlement_id}`} className="border-t border-[#141414] hover:bg-white/5 transition-colors duration-150">
                  <td className="px-5 py-3 text-white tnum text-xs">{s.settlement_id}</td>
                  <td className="px-5 py-3 text-[#A3A3A3] text-xs font-semibold">{s.currency}</td>
                  <td className="px-5 py-3 text-[#A3A3A3] tnum text-xs">{s.period_start} → {s.period_end}</td>
                  <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{fmtMoney(s.sales, s.currency)}</td>
                  <td className="px-5 py-3 text-right tnum text-[#F59E0B]">−{fmtMoney(s.fees, s.currency)}</td>
                  <td className="px-5 py-3 text-right tnum text-[#EF4444]">−{fmtMoney(s.refunds, s.currency)}</td>
                  <td className="px-5 py-3 text-right tnum text-[#10B981] font-bold">{fmtMoney(s.disbursed, s.currency)}</td>
                  <td className="px-5 py-3 text-right">
                    <Input
                      data-testid={`reconcile-input-${s.settlement_id}`}
                      type="number" value={recon[s.settlement_id] ?? ""}
                      placeholder={fmtMoney(s.disbursed, s.currency)}
                      onChange={(e) => setRecon((r) => ({ ...r, [s.settlement_id]: e.target.value }))}
                      onBlur={() => saveRecon(s)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveRecon(s); }}
                      className="h-8 w-28 ml-auto bg-[#111] border-[#262626] text-right text-xs" />
                  </td>
                  <td className="px-5 py-3" data-testid={`reconcile-status-${s.settlement_id}`}>
                    {!hasActual ? <span className="text-[#555] text-xs">not checked</span>
                     : matched ? <span className="inline-flex items-center gap-1 text-[#10B981] text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Matched</span>
                     : <span className="inline-flex items-center gap-1 text-[#F59E0B] text-xs font-semibold" title={`Expected ${fmtMoney(s.disbursed, s.currency)}`}><AlertTriangle className="h-3.5 w-3.5" /> Off by {diff > 0 ? "+" : "−"}{fmtMoney(Math.abs(diff), s.currency)}</span>}
                  </td>
                </tr>
              );})}
              {!data.settlements.length && <tr><td colSpan={9} className="px-5 py-12 text-center text-[#666]">No settlements for {region}. Upload a Settlement / Payments report.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* SKU profitability */}
      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="tx-sku-table">
        <div className="p-5 border-b border-[#1a1a1a]"><h3 className="font-head font-bold text-white">SKU Profitability</h3><p className="text-xs text-[#666]">Revenue − Amazon fees − COGS per SKU ({currency})</p></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
              <th className="px-5 py-3">SKU</th><th className="px-5 py-3">Product</th>
              <th className="px-5 py-3 text-right"><button data-testid="sort-sku-units" onClick={() => sortSku("units")} className="uppercase tracking-wider hover:text-white transition-colors">Units{skuArrow("units")}</button></th>
              <th className="px-5 py-3 text-right"><button data-testid="sort-sku-revenue" onClick={() => sortSku("revenue")} className="uppercase tracking-wider hover:text-white transition-colors">Revenue{skuArrow("revenue")}</button></th>
              <th className="px-5 py-3 text-right"><button data-testid="sort-sku-fees" onClick={() => sortSku("fees")} className="uppercase tracking-wider hover:text-white transition-colors">Fees{skuArrow("fees")}</button></th>
              <th className="px-5 py-3 text-right">COGS</th>
              <th className="px-5 py-3 text-right"><button data-testid="sort-sku-profit" onClick={() => sortSku("net_profit")} className="uppercase tracking-wider hover:text-white transition-colors">Net Profit{skuArrow("net_profit")}</button></th>
              <th className="px-5 py-3 text-right"><button data-testid="sort-sku-margin" onClick={() => sortSku("margin")} className="uppercase tracking-wider hover:text-white transition-colors">Margin{skuArrow("margin")}</button></th>
            </tr></thead>
            <tbody>
              {sortedSkus.map((r) => (
                <tr key={r.sku} data-testid={`sku-row-${r.sku}`} className="border-t border-[#141414] hover:bg-white/5 transition-colors duration-150">
                  <td className="px-5 py-3 text-[#A3A3A3] tnum text-xs">{r.sku}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5 max-w-[240px]">
                      <ProductThumb src={r.image} size={30} title={r.product_name} />
                      <span className="text-white truncate" title={r.product_name}>{r.product_name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{fmtNum(r.units)}</td>
                  <td className="px-5 py-3 text-right tnum text-white">{fmtMoney(r.revenue, currency)}</td>
                  <td className="px-5 py-3 text-right tnum text-[#F59E0B]">{fmtMoney(r.fees, currency)}</td>
                  <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{fmtMoney(r.cogs, currency)}</td>
                  <td className="px-5 py-3 text-right tnum font-bold" style={{ color: r.net_profit >= 0 ? "#10B981" : "#EF4444" }}>{fmtMoney(r.net_profit, currency)}</td>
                  <td className="px-5 py-3 text-right tnum font-semibold" style={{ color: r.margin >= 0 ? "#10B981" : "#EF4444" }}>{r.margin}%</td>
                </tr>
              ))}
              {!data.sku_profitability.length && <tr><td colSpan={8} className="px-5 py-12 text-center text-[#666]">No SKU sales for {region}.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
